// ./core/Audio/WebHtml5Service.ts

import { IAudio } from './IAudio';
import type { AssetManager } from '../Assets';
import { GlobalBus } from '@core/Bus/EventBus';
import {
  sanitizeAudioStorage,
  AUDIO_STORAGE_KEYS,
  DEFAULT_MUSIC_MUTED,
  DEFAULT_SOUND_MUTED,
} from './audioStorage';
import { registerActiveAudio, unregisterActiveAudio } from './audioBus';

export type AudioEngineOptions = {
  slug: string;
  assetManager: AssetManager;
  soundMap?: Record<string, string>;
  /** Per-sound Howler volume overrides (game-specific, e.g. sky-x new). */
  soundVolumes?: Record<string, number>;
  /** When true, lowers bg music volume while SFX are playing (sky-x new). */
  duckMusicOnSfx?: boolean;
  /** Target music volume ratio while ducked (0–1). Default 0.25. */
  musicDuckRatio?: number;
};

export class WebAudioHtml5Backend implements IAudio {
  private slug: string;
  private assets: AssetManager;
  private volume = 1.0;
  private sfxMuted = DEFAULT_SOUND_MUTED;
  private musicMuted = DEFAULT_MUSIC_MUTED;
  private tabHidden = false; // Track if tab is hidden
  private userHasInteracted = false;

  // LocalStorage keys for persistence
  private static readonly STORAGE_KEY_SFX_MUTED = AUDIO_STORAGE_KEYS.SFX;
  private static readonly STORAGE_KEY_MUSIC_MUTED = AUDIO_STORAGE_KEYS.MUSIC;
  private audioContext: AudioContext | null = null;
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private htmlAudioElements: Map<string, HTMLAudioElement> = new Map();
  private activeBufferSources: Map<string, AudioBufferSourceNode[]> = new Map();
  private activeHtmlClones: Map<string, HTMLAudioElement[]> = new Map();

  private currentMusicSource: AudioBufferSourceNode | null = null;
  private musicGainNode: GainNode | null = null;
  private currentMusicElement: HTMLAudioElement | null = null;
  private currentMusicName: string | null = null;
  private musicHealthCheckInterval: ReturnType<typeof setInterval> | null =
    null;
  private lastMusicCheckTime: number = 0;
  private musicStartTime: number = 0;
  private musicDuration: number = 0;
  private lastLoopTime: number = 0;
  private userGestureUnlockListener: (() => void) | null = null;
  private sfxCallbackEpoch = 0;

  private soundMap: Record<string, string> = {
    bet: 'sfx/bet.mp3',
    cashout: 'sfx/cashout.mp3',
    start: 'sfx/start.mp3',
    crash: 'sfx/crash.mp3',
    bgMusic: 'sfx/bg_music.mp3',
  };

  constructor(opts: AudioEngineOptions) {
    this.slug = opts.slug;
    this.assets = opts.assetManager;
    if (opts.soundMap) {
      this.soundMap = { ...this.soundMap, ...opts.soundMap };
    }

    // Load persisted mute states from localStorage
    this.loadPersistedMuteStates();

    this.preloadAllSounds();
    this.initWebAudio();
    this.setupVisibilityListener();
    this.setupUserGestureAudioUnlock();
    registerActiveAudio(this);
  }

  // Load persisted mute states from localStorage
  private loadPersistedMuteStates(): void {
    const snapshot = sanitizeAudioStorage();
    this.sfxMuted = snapshot.sfxMuted;
    this.musicMuted = snapshot.musicMuted;

    console.log(
      '[sound-debug] WebHtml5Service: Loaded mute state snapshot from localStorage',
      {
        sfxMuted: this.sfxMuted,
        musicMuted: this.musicMuted,
        wasReset: snapshot.wasReset,
      }
    );
    console.log(
      '[audio-check] WebAudioHtml5Backend.loadPersisted -> applied snapshot',
      {
        sfxMuted: this.sfxMuted,
        musicMuted: this.musicMuted,
      }
    );
  }

  private async initWebAudio(): Promise<void> {
    if (this.audioContext) return;
    try {
      const AudioContext =
        window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContext();

      this.musicGainNode = this.audioContext.createGain();
      this.musicGainNode.connect(this.audioContext.destination);

      await this.preloadAudioBuffers();
    } catch (err) {
      console.error('[WebAudioHtml5Backend] Failed to init Web Audio:', err);
      this.audioContext = null;
    }
  }

  private async preloadAllSounds(): Promise<void> {
    const soundPromises = Object.entries(this.soundMap).map(
      async ([name, path]) => {
        try {
          await this.assets.loadAsset(this.slug, path);
          const audio = this.assets.getAudio(this.slug, path);
          if (audio) {
            this.htmlAudioElements.set(name, audio);
          }
        } catch (err) {
          console.warn(
            `[WebAudioHtml5Backend] Failed to preload HTML5 audio for ${name}`,
            err
          );
        }
      }
    );
    await Promise.all(soundPromises);
  }

  private async preloadAudioBuffers(): Promise<void> {
    if (!this.audioContext) return;
    const bufferPromises = Object.entries(this.soundMap).map(
      async ([name, path]) => {
        try {
          const fullPath = `${this.assets.baseUrl}/games/${this.slug}/${path}`;
          const response = await fetch(fullPath);
          const arrayBuffer = await response.arrayBuffer();
          const audioBuffer =
            await this.audioContext!.decodeAudioData(arrayBuffer);
          this.audioBuffers.set(name, audioBuffer);
        } catch (err) {
          console.error(
            `[WebAudioHtml5Backend] Failed to decode buffer for ${name}`,
            err
          );
        }
      }
    );
    await Promise.all(bufferPromises);
  }

  play(name: string): void {
    if (this.sfxMuted || this.tabHidden || !this.userHasInteracted) {
      console.log(
        `[WebAudio] Sound "${name}" blocked - sfxMuted: ${this.sfxMuted}, tabHidden: ${this.tabHidden}, interacted: ${this.userHasInteracted}`
      );
      return;
    }
    this.stop(name);
    console.log(`[WebAudio] Playing sound "${name}"`);
    if (this.audioContext?.state === 'running' && this.audioBuffers.has(name)) {
      this.playWithWebAudio(name);
    } else {
      this.playWithHTML5Audio(name);
    }
  }

  stop(name: string): void {
    const sources = this.activeBufferSources.get(name);
    if (sources) {
      for (const source of sources) {
        try {
          source.stop();
        } catch {
          // Already stopped
        }
      }
      this.activeBufferSources.delete(name);
    }

    const clones = this.activeHtmlClones.get(name);
    if (clones) {
      for (const audio of clones) {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch {
          // Already stopped
        }
      }
      this.activeHtmlClones.delete(name);
    }

    const audio = this.htmlAudioElements.get(name);
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        // ignore
      }
    }
  }

  playAt(name: string, seekSeconds: number): void {
    if (this.sfxMuted || this.tabHidden || !this.userHasInteracted) {
      return;
    }
    if (this.isSfxPlaying(name)) {
      this.applyResumableSfxMute(false);
      return;
    }
    const audio = this.htmlAudioElements.get(name);
    if (audio) {
      audio.pause();
      audio.currentTime = Math.max(0, seekSeconds);
      void audio.play();
      return;
    }
    this.play(name);
  }

  getSfxDuration(name: string): number | undefined {
    const audio = this.htmlAudioElements.get(name);
    if (audio && Number.isFinite(audio.duration) && audio.duration > 0) {
      return audio.duration;
    }
    return undefined;
  }

  isSfxPlaying(name: string): boolean {
    const sources = this.activeBufferSources.get(name);
    if (sources && sources.length > 0) {
      return true;
    }
    const clones = this.activeHtmlClones.get(name);
    if (clones?.some(audio => !audio.paused && !audio.ended)) {
      return true;
    }
    const audio = this.htmlAudioElements.get(name);
    return Boolean(audio && !audio.paused && !audio.ended);
  }

  noteUserGesture(): void {
    const wasLocked = !this.userHasInteracted;
    this.userHasInteracted = true;
    if (wasLocked && !this.sfxMuted && !this.tabHidden) {
      GlobalBus.emit('audio:state:changed', { isMuted: false });
    }
  }

  private setupUserGestureAudioUnlock(): void {
    if (this.userGestureUnlockListener) {
      return;
    }

    this.userGestureUnlockListener = () => {
      this.noteUserGesture();
      if (this.audioContext?.state === 'suspended') {
        void this.audioContext.resume();
      }
      if (this.currentMusicName && !this.musicMuted && !this.isMusicPlaying()) {
        this.playMusic(this.currentMusicName);
      }
    };

    document.addEventListener('pointerdown', this.userGestureUnlockListener, {
      capture: true,
    });
    document.addEventListener('click', this.userGestureUnlockListener, {
      capture: true,
    });
    document.addEventListener('touchstart', this.userGestureUnlockListener, {
      capture: true,
    });
    document.addEventListener('keydown', this.userGestureUnlockListener, {
      capture: true,
    });
  }

  playLoop(_name: string): void {
    // Loop SFX not supported on WebAudio backend.
  }

  stopLoop(_name: string): void {
    // Loop SFX not supported on WebAudio backend.
  }

  playWithCallback(name: string, onEnd: () => void): void {
    this.sfxCallbackEpoch += 1;
    const epoch = this.sfxCallbackEpoch;
    const finish = () => {
      if (epoch !== this.sfxCallbackEpoch || this.sfxMuted || this.tabHidden) {
        return;
      }
      onEnd();
    };

    if (this.sfxMuted || this.tabHidden || !this.userHasInteracted) {
      onEnd();
      return;
    }

    this.stop(name);

    if (this.audioContext?.state === 'running' && this.audioBuffers.has(name)) {
      this.playWithWebAudioCallback(name, finish);
    } else {
      this.playWithHTML5AudioCallback(name, finish);
    }
  }

  private playWithWebAudio(name: string) {
    if (!this.audioContext) return;
    const buffer = this.audioBuffers.get(name);
    if (!buffer) return;

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    const gainNode = this.audioContext.createGain();

    // Set individual volumes: higher for start/crash, moderate for others
    let individualVolume = this.volume;
    if (name === 'start') {
      individualVolume = this.volume * 2.0; // Double volume for start sound
    } else if (name === 'crash') {
      individualVolume = this.volume * 1.0; // Normal volume for crash
    }

    gainNode.gain.setValueAtTime(
      individualVolume,
      this.audioContext.currentTime
    );
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    const list = this.activeBufferSources.get(name) ?? [];
    list.push(source);
    this.activeBufferSources.set(name, list);
    source.onended = () => {
      gainNode.disconnect();
      source.disconnect();
      const current = this.activeBufferSources.get(name);
      if (!current) return;
      const next = current.filter(s => s !== source);
      if (next.length === 0) {
        this.activeBufferSources.delete(name);
      } else {
        this.activeBufferSources.set(name, next);
      }
    };

    source.start();

    // For lightning sounds, stop after the lightning animation duration (350ms max)
    if (name === 'lightingBg') {
      setTimeout(() => {
        try {
          source.stop();
          console.log(
            `[WebAudio] Stopped lightning sound "${name}" after animation duration`
          );
        } catch (_err) {
          // Source may already be stopped
        }
      }, 1500); // Match the maximum lightning bolt duration
    }
  }

  private playWithHTML5Audio(name: string) {
    const audio = this.htmlAudioElements.get(name);
    if (audio) {
      const audioClone = audio.cloneNode(true) as HTMLAudioElement;

      // Set individual volumes: higher for start/crash, moderate for others
      let individualVolume = this.volume;
      if (name === 'start') {
        individualVolume = this.volume * 2.0; // Double volume for start sound
      } else if (name === 'crash') {
        individualVolume = this.volume * 1.0; // Normal volume for crash
      } else if (name === 'lightingBg') {
        individualVolume = this.volume * 1.2; // High volume for lightning sound effect
      }

      audioClone.volume = individualVolume;
      audioClone.currentTime = 0;

      const list = this.activeHtmlClones.get(name) ?? [];
      list.push(audioClone);
      this.activeHtmlClones.set(name, list);
      const removeClone = () => {
        const current = this.activeHtmlClones.get(name);
        if (!current) return;
        const next = current.filter(a => a !== audioClone);
        if (next.length === 0) {
          this.activeHtmlClones.delete(name);
        } else {
          this.activeHtmlClones.set(name, next);
        }
      };
      audioClone.addEventListener('ended', removeClone);
      audioClone.addEventListener('pause', () => {
        if (audioClone.currentTime === 0 || audioClone.ended) {
          removeClone();
        }
      });

      audioClone
        .play()
        .catch(() =>
          console.warn(`[WebAudio] HTML5 playback blocked for ${name}`)
        );

      // For lightning sounds, stop after the lightning animation duration (350ms max)
      if (name === 'lightingBg') {
        setTimeout(() => {
          try {
            audioClone.pause();
            audioClone.currentTime = 0;
            console.log(
              `[WebAudio] Stopped lightning sound "${name}" after animation duration`
            );
          } catch (_err) {
            // Audio may already be stopped
          }
        }, 350); // Match the maximum lightning bolt duration
      }
    }
  }

  private setupVisibilityListener(): void {
    // Handle page visibility changes to mute/unmute audio
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Tab is now hidden - pause music
        console.log('[WebAudio] Tab hidden, pausing audio...');
        this.tabHidden = true;

        // Pause HTML5 music if playing
        if (this.currentMusicElement && !this.currentMusicElement.paused) {
          this.currentMusicElement.pause();
        }

        // Suspend AudioContext to save resources
        if (this.audioContext && this.audioContext.state === 'running') {
          this.audioContext.suspend();
        }
      } else {
        // Tab is now visible - resume audio
        console.log('[WebAudio] Tab visible, resuming audio...');
        this.tabHidden = false;

        if (
          this.userHasInteracted &&
          this.audioContext?.state === 'suspended'
        ) {
          void this.audioContext.resume();
        }

        if (this.currentMusicName && !this.musicMuted) {
          this.ensureMusicIsPlaying();
        }
      }
    });
  }

  private startMusicHealthCheck(): void {
    // Clear any existing interval
    this.stopMusicHealthCheck();

    // Check music playback every 5 seconds for aggressive monitoring
    this.musicHealthCheckInterval = setInterval(() => {
      this.ensureMusicIsPlaying();
    }, 5000);

    console.log(
      '[WebAudio] Music health check started (checking every 5 seconds)'
    );
  }

  private stopMusicHealthCheck(): void {
    if (this.musicHealthCheckInterval) {
      clearInterval(this.musicHealthCheckInterval);
      this.musicHealthCheckInterval = null;
      console.log('[WebAudio] Music health check stopped');
    }
  }

  private ensureMusicIsPlaying(): void {
    if (!this.userHasInteracted || !this.currentMusicName || this.musicMuted) {
      return;
    }

    const now = Date.now();
    this.lastMusicCheckTime = now;

    // Timing-based check: if we know the duration and enough time has passed for a loop
    // but we haven't detected a loop, something might be wrong
    if (this.musicDuration > 0 && this.musicStartTime > 0) {
      const timeSinceStart = now - this.musicStartTime;
      const expectedLoops = Math.floor(
        timeSinceStart / (this.musicDuration * 1000)
      );
      const timeSinceLastLoop = now - this.lastLoopTime;

      // If we've been playing longer than 1.5x the duration without a detected loop restart
      if (
        expectedLoops > 0 &&
        timeSinceLastLoop > this.musicDuration * 1000 * 1.5
      ) {
        console.warn(
          `[WebAudio] Music should have looped by now. ` +
            `Duration: ${this.musicDuration}s, Time since last loop: ${(timeSinceLastLoop / 1000).toFixed(1)}s. ` +
            `Restarting music...`
        );
        this.playMusic(this.currentMusicName!);
        return;
      }
    }

    // Check audio context state
    if (this.audioContext) {
      if (this.audioContext.state === 'suspended') {
        console.warn(
          '[WebAudio] Audio context suspended, attempting to resume...'
        );
        this.audioContext
          .resume()
          .then(() => {
            console.log('[WebAudio] Audio context resumed successfully');
            // Check if we need to restart the music
            if (!this.currentMusicSource && !this.currentMusicElement) {
              console.log(
                '[WebAudio] Music not playing after resume, restarting...'
              );
              this.playMusic(this.currentMusicName!);
            }
          })
          .catch(err => {
            console.error('[WebAudio] Failed to resume audio context:', err);
          });
        return;
      }

      // Check if Web Audio source exists and is connected
      if (this.currentMusicSource) {
        // Web Audio API sources don't have a reliable "playing" state check
        // But we can check if the context is running
        if (this.audioContext.state !== 'running') {
          console.warn(
            '[WebAudio] Audio context not running, attempting to resume...'
          );
          this.audioContext.resume();
        }
      }
    }

    // Check HTML5 audio element
    if (this.currentMusicElement) {
      if (this.currentMusicElement.paused || this.currentMusicElement.ended) {
        console.warn('[WebAudio] HTML5 music paused or ended, restarting...');
        this.currentMusicElement.currentTime = 0;
        this.currentMusicElement.play().catch(err => {
          console.error('[WebAudio] Failed to restart HTML5 music:', err);
          // If HTML5 fails, try restarting the music completely
          this.playMusic(this.currentMusicName!);
        });
      }

      // Verify volume and mute state
      if (this.currentMusicElement.volume !== this.volume) {
        this.currentMusicElement.volume = this.volume;
      }
      if (this.currentMusicElement.muted !== this.musicMuted) {
        this.currentMusicElement.muted = this.musicMuted;
      }
      if (!this.currentMusicElement.loop) {
        this.currentMusicElement.loop = true;
      }
    }

    // If no music source exists but we expect one, restart
    if (!this.currentMusicSource && !this.currentMusicElement) {
      console.warn(
        '[WebAudio] No active music source found, restarting music...'
      );
      this.playMusic(this.currentMusicName!);
    }
  }

  playMusic(name: string): void {
    this.currentMusicName = name;

    if (this.musicMuted || !this.userHasInteracted) {
      console.log(
        `[WebAudio] Music "${name}" deferred — muted=${this.musicMuted} interacted=${this.userHasInteracted}`
      );
      return;
    }

    this.stopMusic();
    this.currentMusicName = name;
    this.musicStartTime = Date.now();
    this.lastLoopTime = Date.now();

    console.log('[audio-check] WebAudioHtml5Backend.playMusic -> requested', {
      name,
      musicMuted: this.musicMuted,
      audioContextState: this.audioContext?.state,
      hasBuffer: this.audioBuffers.has(name),
    });

    // Resume audio context if suspended
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().then(() => {
        console.log('[WebAudio] Audio context resumed');
        this.playMusic(name); // Retry after resuming
      });
      return;
    }

    if (
      this.audioContext?.state === 'running' &&
      this.musicGainNode &&
      this.audioBuffers.has(name)
    ) {
      const buffer = this.audioBuffers.get(name);
      if (!buffer) {
        console.error(`[WebAudio] Buffer for music "${name}" not found.`);
        this.currentMusicName = null;
        return;
      }

      // Store the duration for timing-based checks
      this.musicDuration = buffer.duration;
      console.log(
        `[WebAudio] Music duration: ${this.musicDuration.toFixed(2)}s`
      );

      this.currentMusicSource = this.audioContext.createBufferSource();
      this.currentMusicSource.buffer = buffer;
      this.currentMusicSource.loop = true;

      // Add safety handler in case looping fails
      this.currentMusicSource.onended = () => {
        console.warn('[WebAudio] Music ended unexpectedly, restarting...');
        if (!this.musicMuted && this.currentMusicName) {
          setTimeout(() => this.playMusic(name), 100);
        }
      };

      this.currentMusicSource.connect(this.musicGainNode);
      this.currentMusicSource.start();
      this.musicGainNode.gain.setValueAtTime(
        this.musicMuted ? 0 : this.volume,
        this.audioContext.currentTime
      );
      console.log(`[WebAudio] Playing looped music via Web Audio: ${name}`);

      // Start health check to ensure music keeps playing
      this.startMusicHealthCheck();
    } else {
      const audio = this.htmlAudioElements.get(name);
      if (audio) {
        this.currentMusicElement = audio.cloneNode(true) as HTMLAudioElement;
        this.currentMusicElement.volume = this.volume;
        this.currentMusicElement.loop = true;
        this.currentMusicElement.muted = this.musicMuted;

        // Store the duration for timing-based checks
        // Use loadedmetadata event to get accurate duration
        this.currentMusicElement.onloadedmetadata = () => {
          if (this.currentMusicElement) {
            this.musicDuration = this.currentMusicElement.duration;
            console.log(
              `[WebAudio] Music duration: ${this.musicDuration.toFixed(2)}s`
            );
          }
        };

        // Track when audio loops naturally
        this.currentMusicElement.ontimeupdate = () => {
          if (
            this.currentMusicElement &&
            this.currentMusicElement.currentTime < 1 &&
            this.lastLoopTime > 0
          ) {
            // Audio has looped back to the beginning
            const now = Date.now();
            if (now - this.lastLoopTime > this.musicDuration * 1000 * 0.9) {
              console.log('[WebAudio] Natural loop detected');
              this.lastLoopTime = now;
            }
          }
        };

        // Add safety handler for HTML5 audio looping
        this.currentMusicElement.onended = () => {
          console.warn(
            '[WebAudio] HTML5 Music ended unexpectedly, restarting...'
          );
          this.lastLoopTime = Date.now();
          if (
            !this.musicMuted &&
            this.currentMusicElement &&
            this.currentMusicName
          ) {
            this.currentMusicElement.currentTime = 0;
            this.currentMusicElement
              .play()
              .catch(err =>
                console.error('[WebAudio] Failed to restart music:', err)
              );
          }
        };

        // Add additional event listeners for HTML5 audio
        this.currentMusicElement.onpause = () => {
          if (!this.musicMuted && this.currentMusicName) {
            console.warn(
              '[WebAudio] HTML5 Music paused unexpectedly, resuming...'
            );
            this.currentMusicElement
              ?.play()
              .catch(err =>
                console.error('[WebAudio] Failed to resume paused music:', err)
              );
          }
        };

        // If duration is already available (audio was preloaded), use it
        if (audio.duration && !isNaN(audio.duration)) {
          this.musicDuration = audio.duration;
          console.log(
            `[WebAudio] Music duration (preloaded): ${this.musicDuration.toFixed(2)}s`
          );
        }

        this.currentMusicElement
          .play()
          .catch(() => console.warn(`[WebAudio] HTML5 Music playback blocked`));
        console.log(`[WebAudio] Playing looped music via HTML5 Audio: ${name}`);

        // Start health check to ensure music keeps playing
        this.startMusicHealthCheck();
      } else {
        console.warn(`[WebAudio] No audio source found for music: ${name}`);
        this.currentMusicName = null;
      }
    }
  }

  stopMusic(): void {
    this.stopMusicHealthCheck();
    this.currentMusicName = null;
    this.musicStartTime = 0;
    this.musicDuration = 0;
    this.lastLoopTime = 0;

    if (this.currentMusicSource) {
      try {
        this.currentMusicSource.stop();
        this.currentMusicSource.disconnect();
      } catch (err) {
        // Source may already be stopped
        console.warn('[WebAudio] Error stopping music source:', err);
      }
      this.currentMusicSource = null;
    }
    if (this.currentMusicElement) {
      this.currentMusicElement.pause();
      this.currentMusicElement.onended = null;
      this.currentMusicElement.onpause = null;
      this.currentMusicElement.ontimeupdate = null;
      this.currentMusicElement.onloadedmetadata = null;
      this.currentMusicElement = null;
    }
  }

  private playWithWebAudioCallback(name: string, onEnd: () => void) {
    if (!this.audioContext) {
      onEnd();
      return;
    }
    const buffer = this.audioBuffers.get(name);
    if (!buffer) {
      onEnd();
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    const gainNode = this.audioContext.createGain();

    // Set individual volumes
    let individualVolume = this.volume;
    if (name === 'start') {
      individualVolume = this.volume * 2.0;
    } else if (name === 'crash') {
      individualVolume = this.volume * 1.0;
    }

    gainNode.gain.setValueAtTime(
      individualVolume,
      this.audioContext.currentTime
    );
    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    // Call onEnd when sound finishes
    source.onended = () => {
      gainNode.disconnect();
      source.disconnect();
      onEnd();
    };

    source.start();

    // For lightning sounds, stop after animation duration
    if (name === 'lightingBg') {
      setTimeout(() => {
        try {
          source.stop();
        } catch (_err) {
          // Source may already be stopped
        }
      }, 1500);
    }
  }

  private playWithHTML5AudioCallback(name: string, onEnd: () => void) {
    const audio = this.htmlAudioElements.get(name);
    if (!audio) {
      onEnd();
      return;
    }

    const audioClone = audio.cloneNode(true) as HTMLAudioElement;

    // Set individual volumes
    let individualVolume = this.volume;
    if (name === 'start') {
      individualVolume = this.volume * 2.0;
    } else if (name === 'crash') {
      individualVolume = this.volume * 1.0;
    } else if (name === 'lightingBg') {
      individualVolume = this.volume * 1.2;
    }

    audioClone.volume = individualVolume;
    audioClone.currentTime = 0;

    // Call onEnd when sound finishes
    audioClone.onended = () => {
      onEnd();
    };

    audioClone.play().catch(() => {
      console.warn(`[WebAudio] HTML5 playback blocked for ${name}`);
      onEnd();
    });

    // For lightning sounds, stop after animation duration
    if (name === 'lightingBg') {
      setTimeout(() => {
        try {
          audioClone.pause();
          audioClone.currentTime = 0;
        } catch (_err) {
          // Audio may already be stopped
        }
      }, 350);
    }
  }

  setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));

    if (this.musicGainNode && this.audioContext && !this.musicMuted) {
      this.musicGainNode.gain.setValueAtTime(
        this.volume,
        this.audioContext.currentTime
      );
    }
    if (this.currentMusicElement) {
      this.currentMusicElement.volume = this.volume;
    }
  }

  getVolume(): number {
    return this.volume;
  }

  private isMusicTrackName(name: string): boolean {
    return (
      name.includes('bgMusic') ||
      name.includes('music') ||
      name === 'dogLoading'
    );
  }

  private isResumableSfx(name: string): boolean {
    return name === 'start';
  }

  private applyResumableSfxMute(muted: boolean): void {
    const clones = this.activeHtmlClones.get('start');
    if (clones) {
      for (const audio of clones) {
        audio.muted = muted;
      }
    }
    const audio = this.htmlAudioElements.get('start');
    if (audio) {
      audio.muted = muted;
    }
  }

  private stopAllPlayingSfx(): void {
    this.sfxCallbackEpoch += 1;
    const names = new Set<string>([
      ...this.audioBuffers.keys(),
      ...this.htmlAudioElements.keys(),
      ...this.activeBufferSources.keys(),
      ...this.activeHtmlClones.keys(),
    ]);
    for (const name of names) {
      if (this.isMusicTrackName(name)) {
        continue;
      }
      if (this.isResumableSfx(name)) {
        this.applyResumableSfxMute(true);
        continue;
      }
      this.stop(name);
    }
  }

  muteSfx(): void {
    this.sfxMuted = true;
    this.applyResumableSfxMute(true);
    this.stopAllPlayingSfx();
  }
  unmuteSfx(): void {
    this.sfxMuted = false;
    this.applyResumableSfxMute(false);
  }
  isSfxMuted(): boolean {
    return this.sfxMuted;
  }

  muteMusic(): void {
    this.musicMuted = true;
    if (this.musicGainNode && this.audioContext) {
      this.musicGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    }
    if (this.currentMusicElement) {
      this.currentMusicElement.pause();
      this.currentMusicElement.muted = true;
    }
  }

  unmuteMusic(): void {
    this.musicMuted = false;
    if (this.musicGainNode && this.audioContext) {
      this.musicGainNode.gain.setValueAtTime(
        this.volume,
        this.audioContext.currentTime
      );
    }
    if (this.currentMusicElement) this.currentMusicElement.muted = false;
    if (
      this.currentMusicName &&
      this.userHasInteracted &&
      !this.isMusicPlaying()
    ) {
      this.playMusic(this.currentMusicName);
    }
  }
  isMusicMuted(): boolean {
    return this.musicMuted;
  }

  isMusicPlaying(): boolean {
    if (this.musicMuted) {
      return false;
    }
    if (this.currentMusicElement) {
      return (
        !this.currentMusicElement.paused && !this.currentMusicElement.ended
      );
    }
    return this.currentMusicSource !== null;
  }

  debugAudioStatus(): void {
    console.log('[WebAudioHtml5Backend] Status', {
      slug: this.slug,
      volume: this.volume,
      sfxMuted: this.sfxMuted,
      musicMuted: this.musicMuted,
      audioContext: this.audioContext?.state ?? 'NOT CREATED',
    });
  }

  dispose(): void {
    unregisterActiveAudio(this);
    if (this.userGestureUnlockListener) {
      document.removeEventListener(
        'pointerdown',
        this.userGestureUnlockListener,
        true
      );
      document.removeEventListener(
        'click',
        this.userGestureUnlockListener,
        true
      );
      document.removeEventListener(
        'touchstart',
        this.userGestureUnlockListener,
        true
      );
      document.removeEventListener(
        'keydown',
        this.userGestureUnlockListener,
        true
      );
      this.userGestureUnlockListener = null;
    }
    this.stopMusicHealthCheck();
    this.stopMusic();
  }
}
